import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { authService } from '@/services/api'
import { forgotPasswordFormSchema, defaultForgotPasswordValues } from './ForgotPasswordForm.static'
import type { ForgotPasswordFormValues } from './ForgotPasswordForm.static'
import type { ApiError } from '@/types'

export function useForgotPasswordForm() {
  const { t } = useTranslation('auth')
  const [isSuccess, setIsSuccess] = useState(false)

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: defaultForgotPasswordValues,
    mode: 'onBlur',
  })

  const forgotPasswordMutation = useMutation({
    mutationFn: authService.forgotPassword,
    onSuccess: () => {
      setIsSuccess(true)
    },
    onError: (error: AxiosError<ApiError>) => {
      const message =
        error.response?.status === 429
          ? t('errors.rateLimitExceeded')
          : t('errors.forgotPasswordFailed')
      form.setError('root', {
        type: 'manual',
        message,
      })
    },
  })

  const onSubmit = (data: ForgotPasswordFormValues) => {
    forgotPasswordMutation.mutate({ email: data.email })
  }

  return {
    t,
    form,
    isLoading: forgotPasswordMutation.isPending,
    isSuccess,
    onSubmit,
  }
}
